import React from "react";
import {history, Nullable} from "@data-force/app";
import {match, matchPath} from "react-router";
import Icon from "@ant-design/icons";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import {TranslateFunction} from "react-localize-redux";
import HistoryParams from "../interfaces/historyParams";
import {
    AnnotationIcon,
    CollectionIcon,
    ExternalIcon,
    MobileIcon,
    NeaIcon,
    SegmentationIcon,
    TranscriptionIcon,
} from "../assets/Icons";
import {TaskPhaseStats} from "../interfaces/store/actions/payload/task";
import {
    IconJobType, LocalePrefix, PhaseStatus, ProjectType,
} from "../enums/enums";
import {JobEntity, PhaseConfigNormalized} from "../interfaces/store/actions/payload/job";
import {Editors} from "../interfaces/common";

dayjs.extend(duration);

/**
 * Regex for searching placeholders in string template
 */
const formatStringRegex: RegExp = /{(\d+)}/g;

/**
 * Format string with values and template
 * @param {string} template
 * @param {string[]} values
 * @return {string}
 */
const formatString = (template: string, ...values: string[]): string => template.replace(
    formatStringRegex, (test: string, index: number): string => (typeof values[index] !== "undefined" ? values[index] : test),
);

const getHistoryParams = (): Nullable<HistoryParams> => {
    const matchResult: Nullable<match<HistoryParams>> = matchPath(history.location.pathname, "/:idpId/:view?/:projectId?");
    return matchResult?.params;
};

/**
 * Returns job icon for given editor type
 * @param {string} type
 * @return {JSX.Element} icon
 */
function getJobIconType(type: string): JSX.Element {
    const {
        ANNOTATION, TRANSCRIPTION, MOBILE, SEGMENTATION, COLLECTION, EXTERNAL, NEA,
    }: typeof IconJobType = IconJobType;
    let icon: React.FC = AnnotationIcon;

    switch (type) {
        case ANNOTATION:
            icon = AnnotationIcon;
            break;
        case TRANSCRIPTION:
            icon = TranscriptionIcon;
            break;
        case MOBILE:
            icon = MobileIcon;
            break;
        case NEA:
            icon = NeaIcon;
            break;
        case SEGMENTATION:
            icon = SegmentationIcon;
            break;
        case COLLECTION:
            icon = CollectionIcon;
            break;
        case EXTERNAL:
            icon = ExternalIcon;
            break;
        default:
            break;
    }

    return <Icon component={icon}/>;
}

/**
 * Calculates status of job in phase for which stats are passed
 * @param {JobEntity} data
 * @return {string} task status
 */
function getJobPhaseStatus(data: JobEntity): string {
    const {
        phaseStats, completedTaskCount, assignee,
    }: JobEntity = data;
    let jobPhaseStatus: string = PhaseStatus.IN_PROGRESS;
    if (phaseStats) {
        const {
            FAILED = 0,
            NOT_STARTED = 0,
            READY = 0,
            REJECTED = 0,
            REOPENED = 0,
            SKIPPED = 0,
            IN_PROGRESS = 0,
            COMPLETED = 0,
        }: TaskPhaseStats = phaseStats;
        // Tasks count is sum of all except skipped because that status should be excluded from job status calculation
        const taskCount: number = COMPLETED + FAILED + IN_PROGRESS + NOT_STARTED + READY + REJECTED + REOPENED;
        if (taskCount) {
            if (taskCount === FAILED) {
                jobPhaseStatus = PhaseStatus.FAILED;
            } else if (taskCount === completedTaskCount && COMPLETED) {
                // completedTaskCount represents total of rejected and completed tasks,
                // hence, if that number is equal to taskCount and at least one of completed tasks is in completed status
                // we mark whole phase as completed
                jobPhaseStatus = PhaseStatus.COMPLETED;
            } else if (taskCount === REJECTED) {
                // if number of task count is equal to number of rejected ones, we mark the phase as rejected
                jobPhaseStatus = PhaseStatus.REJECTED;
            } else if (taskCount === REOPENED) {
                // if number of all tasks is equal to number of rejected ones, we mark the phase as reopened
                jobPhaseStatus = PhaseStatus.REOPENED;
            } else if (taskCount === NOT_STARTED) {
                // When job hasn't been claimed or assigned he wont have assignee value and should be marked as new.
                // If phase has no assignee value it should be marked as new
                jobPhaseStatus = assignee ? PhaseStatus.NOT_STARTED : PhaseStatus.NEW;
            } else if (taskCount === READY || taskCount === (READY + NOT_STARTED)) {
                // When job hasn't been claimed or assigned he wont have assignee value and should be marked as new.
                // Phase that has at least one task ready to be processed should be marked as ready
                jobPhaseStatus = assignee ? PhaseStatus.READY : PhaseStatus.NEW;
            }
        } else if (SKIPPED) {
            // if task count is zero and there is skipped ones we mark whole phase as rejected
            jobPhaseStatus = PhaseStatus.REJECTED;
        }
    }

    return jobPhaseStatus.toLowerCase();
}

/**
 * Get opening url for editor
 * @param {Editors} editors
 * @param {PhaseConfigNormalized} phaseConfigList
 * @param {JobEntity} data
 * @return string
 */
function getOpeningUrl(editors: Editors, phaseConfigList: PhaseConfigNormalized, data: JobEntity): string {
    let openingUrl: string = "";
    if (data) {
        const {
            name: phase, projectId, jobId, id: jobPhaseId, projectType, phaseStats,
        }: JobEntity = data;
        const editor: string = phaseConfigList[`${projectType}-${phase}`]?.editor;

        if (editor && editor !== "mobile" && phaseStats) {
            // Jobs with no tasks should not be opened in editor
            const editorUrl: string = editors[editor];
            const historyParams: Nullable<HistoryParams> = getHistoryParams();
            openingUrl = `${editorUrl}/#/${historyParams?.idpId}/${phase}/${projectId}/${jobId}/${jobPhaseId}`;
        }
    }

    return openingUrl;
}

/**
 * Returns formatted estimated time,
 * if estimated time is under 1hour we return X-minutes,
 * if there is full hours we return X-hours format,
 * if there are hours with minutes, we return X-hours Y-minutes format
 * @param {number} time
 * @param {TranslateFunction} translate
 * @return {string}
 */
function getFormattedTime(time: number, translate: TranslateFunction): string {
    const estimatedTime: number = dayjs.duration(time * 1000).asMinutes();
    const hours:number = Math.floor(estimatedTime / 60);
    const minutes:number = estimatedTime % 60;
    let formattedTime: string;
    const minutesFormat: string = translate("minutesFormat.text") as string;
    const hoursFormat: string = translate("hoursFormat.text") as string;
    const hoursMinutesFormat: string = translate("hoursMinutesFormat.text") as string;

    if (estimatedTime < 60) {
        formattedTime = formatString(minutesFormat, estimatedTime.toString());
    } else if (minutes) {
        formattedTime = formatString(hoursMinutesFormat, hours.toString(), minutes.toString());
    } else {
        formattedTime = formatString(hoursFormat, hours.toString());
    }

    return formattedTime;
}

/**
 * Depending on job type, we return locale key for jobs/tests
 * @param {Nullable<boolean>} isTest
 * @return {string}
 */
const getLocalePrefix = (isTest: Nullable<boolean>): string => (isTest ? LocalePrefix.TEST : LocalePrefix.JOB);

/**
 * Checks if the given projectType is an external project and returns a boolean value
 *
 * @param {Nullable<string>} projectType
 * @return {boolean}
 */
const isExternalProject = (projectType: Nullable<string>): boolean => projectType === ProjectType.EXTERNAL;

/**
 * From job entity data we collect available clones count, this count,
 * when used in tests context, has meaning of number of retakes for a given test
 * @param {JobEntity} data
 * @return {number}
 */
const getAvailableInfoCount = (data: JobEntity): number => {
    const {
        cloneCount = 0,
        cloneLimit = 0,
        userCloneLimit = 0,
        userCloneCount = 0,
    }: JobEntity = data;

    const leftoverClones: number = cloneLimit - cloneCount;
    let availableClonesCount: number;

    if (userCloneCount) {
        // userCloneCount represent existence of at least one cloned job
        if (userCloneLimit <= leftoverClones) {
            availableClonesCount = userCloneLimit - userCloneCount;
        } else if (userCloneLimit === userCloneCount) {
            availableClonesCount = 0;
        } else {
            availableClonesCount = leftoverClones;
        }
    } else {
        availableClonesCount = leftoverClones > cloneCount ? userCloneLimit : leftoverClones;
    }

    return availableClonesCount;
};

/**
 * Based on document query for custom attr 'df-current' we find if there are any elements that need to be focused.
 * When element is focused, we remove the custom attr from the element, allowing for the next element to be focused.
 */
const setFocusedElement = (): void => {
    const currentEl: HTMLElement = document.querySelector("[data-focus='df-current']") as HTMLElement;
    if (currentEl) {
        currentEl.focus();
        currentEl.removeAttribute("data-focus");
    }
};

export {
    formatString,
    getHistoryParams,
    getJobIconType,
    getJobPhaseStatus,
    getOpeningUrl,
    getFormattedTime,
    getLocalePrefix,
    isExternalProject,
    getAvailableInfoCount,
    setFocusedElement,
};
